import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Trash2, Pencil, X, Bell, Image as ImageIcon, ClipboardList } from 'lucide-react';
import { API } from '../../content/data';
import { MediaPicker } from './design';

const inputCls = 'w-full rounded-xl border border-navy/15 bg-white px-4 py-3 text-sm text-navy outline-none transition-colors focus:border-electric placeholder:text-navy/40';
const labelCls = 'overline !text-[10px] mb-1.5 block text-navy/50';

const TYPE_META = {
  notification: { label: 'Notification', icon: Bell, desc: 'Corner toast-style card' },
  image: { label: 'Image Popup', icon: ImageIcon, desc: 'Centered visual with optional CTA' },
  form: { label: 'Form Popup', icon: ClipboardList, desc: 'Lead capture inside the popup' },
};

const EMPTY = { type: 'notification', title: '', body: '', image: '', ctaLabel: '', ctaUrl: '', delaySeconds: 3, active: true };

export function PopupsSection({ adminKey, onAuthError }) {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const headers = { 'X-Admin-Key': adminKey };

  const load = useCallback(() => {
    axios.get(`${API}/admin/popups`, { headers }).then((r) => setList(r.data)).catch((e) => { if (e.response?.status === 401) onAuthError(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminKey]);
  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setEditing((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...editing, delaySeconds: Number(editing.delaySeconds) || 0 };
      if (editing.id) await axios.put(`${API}/admin/popups/${editing.id}`, payload, { headers });
      else await axios.post(`${API}/admin/popups`, payload, { headers });
      setEditing(null);
      load();
    } finally { setSaving(false); }
  };

  const toggle = async (p) => {
    await axios.put(`${API}/admin/popups/${p.id}`, { ...p, active: !p.active }, { headers });
    load();
  };

  return (
    <div data-testid="admin-popups-section">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-h3 font-semibold text-navy">Popups</h2>
          <p className="mt-1 text-sm text-navy/55">Popup notifications, images and lead forms. Each visitor sees a popup once per session.</p>
        </div>
        <button data-testid="popup-new-button" onClick={() => setEditing({ ...EMPTY })} className="flex items-center gap-2 rounded-full bg-electric px-5 py-2.5 text-xs font-semibold text-white transition-colors hover:bg-navy">
          <Plus className="h-3.5 w-3.5" /> New Popup
        </button>
      </div>

      <div className="mb-6 grid gap-3 sm:grid-cols-3">
        {Object.entries(TYPE_META).map(([k, m]) => (
          <button key={k} data-testid={`popup-type-${k}`} onClick={() => setEditing({ ...EMPTY, type: k })} className="rounded-3xl border border-navy/10 bg-white p-5 text-left transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-lg">
            <m.icon className="h-5 w-5 text-electric" />
            <p className="mt-2.5 text-sm font-semibold text-navy">{m.label}</p>
            <p className="mt-1 text-xs text-navy/55">{m.desc}</p>
          </button>
        ))}
      </div>

      {editing && (
        <div data-testid="popup-editor" className="mb-6 grid gap-3 rounded-3xl border border-navy/10 bg-white p-6 sm:grid-cols-2">
          <div><span className={labelCls}>Type</span>
            <select data-testid="popup-type-select" className={inputCls} value={editing.type} onChange={set('type')}>
              {Object.entries(TYPE_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
            </select>
          </div>
          <div><span className={labelCls}>Show after (seconds)</span><input type="number" min="0" max="120" className={inputCls} value={editing.delaySeconds} onChange={set('delaySeconds')} /></div>
          <div className="sm:col-span-2"><span className={labelCls}>Title</span><input data-testid="popup-title-input" className={inputCls} value={editing.title} onChange={set('title')} /></div>
          <div className="sm:col-span-2"><span className={labelCls}>Body text</span><textarea rows={2} className={`${inputCls} resize-y`} value={editing.body} onChange={set('body')} /></div>
          {editing.type === 'image' && (
            <div className="sm:col-span-2">
              <span className={labelCls}>Popup image</span>
              <input className={`${inputCls} font-mono`} value={editing.image || ''} onChange={set('image')} placeholder="/api/uploads/… or https://…" />
              <MediaPicker adminKey={adminKey} kinds={['image']} onPick={(url) => setEditing((f) => ({ ...f, image: url }))} testId="popup-image-browse" />
            </div>
          )}
          {editing.type !== 'form' && (
            <>
              <div><span className={labelCls}>CTA label (optional)</span><input className={inputCls} value={editing.ctaLabel || ''} onChange={set('ctaLabel')} placeholder="Learn more" /></div>
              <div><span className={labelCls}>CTA link</span><input className={inputCls} value={editing.ctaUrl || ''} onChange={set('ctaUrl')} placeholder="/scholarship or https://…" /></div>
            </>
          )}
          {editing.type === 'form' && (
            <div className="sm:col-span-2"><span className={labelCls}>Submit button label (optional)</span><input className={inputCls} value={editing.ctaLabel || ''} onChange={set('ctaLabel')} placeholder="Submit" /></div>
          )}
          <label className="flex items-center gap-3 text-sm font-semibold text-navy">
            <input data-testid="popup-active-toggle" type="checkbox" checked={!!editing.active} onChange={set('active')} className="h-5 w-5 accent-electric" /> Active (visible on the site)
          </label>
          <div className="flex gap-2 sm:justify-end">
            <button onClick={() => setEditing(null)} className="flex items-center gap-1.5 rounded-full border border-navy/15 px-5 py-2.5 text-xs font-semibold text-navy"><X className="h-3.5 w-3.5" /> Cancel</button>
            <button data-testid="popup-save-button" onClick={save} disabled={saving || !editing.title} className="rounded-full bg-electric px-6 py-2.5 text-xs font-semibold text-white hover:bg-navy disabled:opacity-50">{saving ? 'Saving…' : 'Save Popup'}</button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {list.map((p) => {
          const Meta = TYPE_META[p.type] || TYPE_META.notification;
          return (
            <div key={p.id} className="flex flex-wrap items-center gap-4 rounded-2xl border border-navy/10 bg-white p-5">
              <Meta.icon className="h-4 w-4 shrink-0 text-electric" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-navy">{p.title}</p>
                <p className="mt-1 font-mono text-[11px] text-navy/45">{Meta.label} · after {p.delaySeconds ?? 3}s</p>
              </div>
              <button data-testid={`popup-toggle-${p.id.slice(0, 8)}`} onClick={() => toggle(p)} className={`rounded-full px-3 py-1 text-[11px] font-semibold transition-colors ${p.active ? 'bg-electric/10 text-electric' : 'bg-navy/10 text-navy/50'}`}>
                {p.active ? 'Active' : 'Off'}
              </button>
              <button data-testid={`popup-edit-${p.id.slice(0, 8)}`} onClick={() => setEditing(p)} className="flex items-center gap-1.5 rounded-full border border-navy/15 px-4 py-2 text-xs font-semibold text-navy hover:border-electric hover:text-electric"><Pencil className="h-3.5 w-3.5" /> Edit</button>
              <button data-testid={`popup-delete-${p.id.slice(0, 8)}`} onClick={async () => { if (window.confirm('Delete this popup?')) { await axios.delete(`${API}/admin/popups/${p.id}`, { headers }); load(); } }} className="rounded-full border border-red-200 p-2 text-red-600 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          );
        })}
        {list.length === 0 && !editing && <p className="rounded-3xl border border-navy/10 bg-white p-10 text-center text-sm text-navy/50">No popups yet — create a notification, image or form popup above.</p>}
      </div>
    </div>
  );
}
